import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import client from '../../api/client';
import {
  fetchWorksheetById,
  updateWorksheet,
  deleteWorksheet,
  addQuestion,
  updateQuestion as apiUpdateQuestion,
  deleteQuestion as apiDeleteQuestion,
  reorderQuestions,
  addQuestionStandard,
  removeQuestionStandard,
} from '../../api/worksheets';
import WorksheetDocument from './WorksheetDocument';
import QuestionStepper from '../QuestionStepper';
import styles from './WorksheetEditorPage.module.css';

function WorksheetEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Worksheet state
  const [worksheetId, setWorksheetId] = useState(id ? parseInt(id, 10) : null);
  const [worksheet, setWorksheet] = useState({
    title: '',
    type: 'quiz',
    subject: '',
    gradeLevel: '',
    difficulty: 'medium',
    instructions: '',
  });
  const [questions, setQuestions] = useState([]);
  const [conversationId, setConversationId] = useState(null);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [pendingQuestions, setPendingQuestions] = useState(null);

  // UI state
  const [documentVisible, setDocumentVisible] = useState(!!id);
  const [pageLoading, setPageLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const messagesEndRef = useRef(null);

  // Load existing worksheet
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const result = await fetchWorksheetById(parseInt(id, 10));
        const ws = result.data || result.worksheet || result;
        setWorksheet({
          title: ws.title || '',
          type: ws.type || 'quiz',
          subject: ws.subject || '',
          gradeLevel: ws.gradeLevel || '',
          difficulty: ws.difficulty || 'medium',
          instructions: ws.instructions || '',
        });
        setQuestions(ws.questions || []);
        setConversationId(ws.conversationId || null);
        setDocumentVisible(true);
      } catch (err) {
        setError('Failed to load worksheet');
      } finally {
        setPageLoading(false);
      }
    })();
  }, [id]);

  // Apply worksheet updates from chat response
  const applyWorksheetUpdates = useCallback(
    (updates) => {
      for (const update of updates) {
        switch (update.type) {
          case 'worksheetCreated':
            setWorksheet((prev) => ({ ...prev, ...update.worksheet }));
            setWorksheetId(update.worksheet.id);
            setDocumentVisible(true);
            navigate(`/worksheets/${update.worksheet.id}/edit`, { replace: true });
            break;
          case 'worksheetQuestionsUpdated':
            setQuestions(update.questions);
            setDocumentVisible(true);
            break;
          case 'questionUpdated':
            setQuestions((prev) =>
              prev.map((q) => (q.id === update.question.id ? update.question : q))
            );
            break;
          case 'questionDeleted':
            setQuestions((prev) => prev.filter((q) => q.id !== update.questionId));
            break;
          case 'worksheetUpdated':
            setWorksheet((prev) => ({ ...prev, ...update.worksheet }));
            break;
          case 'questionsReordered':
            if (update.questions) setQuestions(update.questions);
            break;
          default:
            break;
        }
      }
    },
    [navigate]
  );

  // Send chat message
  const sendMessage = async (text) => {
    if (!text.trim() || chatLoading) return;

    const userMessage = { role: 'user', text: text.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setChatLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await client.post(
        '/api/chat',
        {
          message: text.trim(),
          conversationId,
          mode: 'worksheet',
          worksheetId,
        },
        { headers }
      );

      const data = response.data;

      if (data.conversationId) setConversationId(data.conversationId);

      // Add assistant message
      if (data.text) {
        setMessages((prev) => [...prev, { role: 'assistant', text: data.text }]);
      }

      // Apply worksheet updates
      if (data.worksheetUpdates?.length > 0) {
        applyWorksheetUpdates(data.worksheetUpdates);
      }

      // Handle agent choices (QuestionStepper)
      if (data.lessonPlanUpdates) {
        const choicesUpdates = data.lessonPlanUpdates.filter(
          (u) => u.section === 'agentChoices'
        );
        const choicesUpdate =
          choicesUpdates.length > 0
            ? choicesUpdates[choicesUpdates.length - 1]
            : null;
        if (choicesUpdate) {
          setPendingQuestions(choicesUpdate.content.questions);
        } else {
          setPendingQuestions(null);
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' },
      ]);
    } finally {
      setChatLoading(false);
    }
  };

  // Question CRUD handlers (for document panel direct edits)
  const handleUpdateWorksheet = (field, value) => {
    setWorksheet((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateQuestion = async (questionId, data) => {
    if (worksheetId) {
      try {
        await apiUpdateQuestion(worksheetId, questionId, data);
        setQuestions((prev) =>
          prev.map((q) => (q.id === questionId ? { ...q, ...data } : q))
        );
      } catch (err) {
        setError('Failed to update question');
      }
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (worksheetId) {
      try {
        await apiDeleteQuestion(worksheetId, questionId);
        setQuestions((prev) => prev.filter((q) => q.id !== questionId));
      } catch (err) {
        setError('Failed to delete question');
      }
    }
  };

  const handleAddQuestion = async (questionType) => {
    if (worksheetId) {
      try {
        const result = await addQuestion(worksheetId, {
          questionType,
          questionText: '',
          difficulty: 'medium',
        });
        setQuestions((prev) => [...prev, result.data || result.question]);
      } catch (err) {
        setError('Failed to add question');
      }
    }
  };

  const handleAddQuestionStandard = async (questionId, standard) => {
    if (worksheetId) {
      try {
        await addQuestionStandard(worksheetId, questionId, standard.id);
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === questionId
              ? {
                  ...q,
                  questionStandards: [
                    ...(q.questionStandards || []),
                    { standard, isPrimary: true },
                  ],
                }
              : q
          )
        );
      } catch (err) {
        setError('Failed to link standard');
      }
    }
  };

  const handleRemoveQuestionStandard = async (questionId, standardId) => {
    if (worksheetId) {
      try {
        await removeQuestionStandard(worksheetId, questionId, standardId);
        setQuestions((prev) =>
          prev.map((q) =>
            q.id === questionId
              ? {
                  ...q,
                  questionStandards: (q.questionStandards || []).filter(
                    (qs) => qs.standard.id !== standardId
                  ),
                }
              : q
          )
        );
      } catch (err) {
        setError('Failed to unlink standard');
      }
    }
  };

  // Save worksheet
  const handleSave = async () => {
    if (!worksheetId) return;
    setSaving(true);
    try {
      await updateWorksheet(worksheetId, worksheet);
    } catch (err) {
      setError('Failed to save worksheet');
    } finally {
      setSaving(false);
    }
  };

  // Delete worksheet
  const handleDelete = async () => {
    if (!worksheetId || !window.confirm('Delete this worksheet?')) return;
    try {
      await deleteWorksheet(worksheetId);
      navigate('/worksheets');
    } catch (err) {
      setError('Failed to delete worksheet');
    }
  };

  // Auto-scroll messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  // QuestionStepper answer handler
  const handleChoicesAnswer = useCallback(
    (answers) => {
      setPendingQuestions(null);
      const parts = [];
      if (Array.isArray(answers)) {
        answers.forEach((a) => {
          if (a !== null) {
            const val = Array.isArray(a) ? a.join(', ') : a;
            parts.push(val);
          }
        });
      }
      if (parts.length > 0) {
        sendMessage(parts.join(', '));
      }
    },
    [conversationId, worksheetId, chatLoading] // eslint-disable-line react-hooks/exhaustive-deps
  );

  if (pageLoading) {
    return <div className={styles.loading}>Loading worksheet...</div>;
  }

  return (
    <div className={styles.container}>
      {/* Chat Panel — always visible */}
      <div className={`${styles.chatPanel} ${!documentVisible ? styles.chatPanelFull : ''}`}>
        <div className={styles.chatHeader}>
          <button
            onClick={() => navigate('/worksheets')}
            className={styles.backButton}
            aria-label="Back"
          >
            &larr; Back
          </button>
          <h2 className={styles.chatTitle}>Worksheet Chat</h2>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.messagesContainer}>
          {messages.length === 0 && (
            <div className={styles.welcomeContainer}>
              <h3 className={styles.welcomeTitle}>Worksheet Assistant</h3>
              <p className={styles.welcomeText}>
                Tell me about the worksheet you want to create!
              </p>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={`${styles.message} ${
                msg.role === 'user' ? styles.userMessage : styles.assistantMessage
              }`}
            >
              <div className={styles.messageContent}>
                <span className={styles.messageRole}>
                  {msg.role === 'user' ? 'You' : 'Assistant'}
                </span>
                <p className={styles.messageText}>{msg.text}</p>
              </div>
            </div>
          ))}

          {pendingQuestions && !chatLoading && (
            <div className={styles.choiceContainer}>
              <QuestionStepper
                questions={pendingQuestions}
                onComplete={handleChoicesAnswer}
                disabled={chatLoading}
              />
            </div>
          )}

          {chatLoading && (
            <div className={styles.loadingMessage}>Thinking...</div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className={styles.inputArea}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) =>
              e.key === 'Enter' && !e.shiftKey && sendMessage(input)
            }
            placeholder="Ask about your worksheet..."
            disabled={chatLoading}
            className={styles.chatInput}
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={chatLoading || !input.trim()}
            className={styles.sendButton}
          >
            Send
          </button>
        </div>
      </div>

      {/* Document Panel — shown when worksheet exists */}
      {documentVisible && (
        <div className={styles.documentPanel}>
          <div className={styles.documentWrapper}>
            <WorksheetDocument
              worksheet={worksheet}
              questions={questions}
              onUpdate={handleUpdateWorksheet}
              onUpdateQuestion={handleUpdateQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onAddQuestion={handleAddQuestion}
              onAddQuestionStandard={handleAddQuestionStandard}
              onRemoveQuestionStandard={handleRemoveQuestionStandard}
            />
          </div>

          <div className={styles.saveBar}>
            <div className={styles.saveActions}>
              <button
                onClick={() => navigate('/worksheets/new')}
                className={styles.newButton}
              >
                New Worksheet
              </button>
              <button
                onClick={() => navigate(`/worksheets/${worksheetId}/print`)}
                className={styles.printButton}
                disabled={!worksheetId}
              >
                Print
              </button>
              <button
                onClick={handleDelete}
                className={styles.deleteButton}
                disabled={!worksheetId}
              >
                Delete
              </button>
              <button
                onClick={handleSave}
                className={styles.saveButton}
                disabled={saving || !worksheetId}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WorksheetEditorPage;
